import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Container,
    Paper,
    Typography,
    Button,
    TextField,
    Box,
    Alert,
    Grid,
    Avatar
} from "@mui/material";
import { Palette as PaletteIcon, Upload as UploadIcon, Save as SaveIcon } from "@mui/icons-material";
import AppHeader from "../../components/AppHeader";
import { useTenantContext } from "../../context/TenantContext";

const PRESET_COLORS = [
    "#1976d2", // Blue
    "#d32f2f", // Red
    "#388e3c", // Green
    "#fbc02d", // Yellow
    "#7b1fa2", // Purple
    "#00796b", // Teal
    "#e64a19", // Deep Orange
    "#455a64", // Blue Grey
    "primary.main", // Indigo
    "#0f172a"  // Slate
];

const BrandingSetupPage = ({ setIsLoggedIn }) => {
    const { tenantProfile, refreshProfile } = useTenantContext();
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ type: "", text: "" });

    const [form, setForm] = useState({
        tenant_name: "",
        logo_url: "",
        primary_color: "#1976d2"
    });

    const tenantHeader = { "X-Tenant": localStorage.getItem("tenant") || "" };

    useEffect(() => {
        if (tenantProfile) {
            setForm({
                tenant_name: tenantProfile.tenant_name || "",
                logo_url: tenantProfile.logo_url || "/logo.png",
                primary_color: tenantProfile.primary_color || "#1976d2"
            });
        }
    }, [tenantProfile]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) { // 2MB limit
                setMsg({ type: "error", text: "Logo file must be less than 2MB" });
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setForm(prev => ({ ...prev, logo_url: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        setMsg({ type: "", text: "" });
        try {
            const res = await axios.put(`${process.env.REACT_APP_API_URL1}/system/tenant-profile`, form, {
                withCredentials: true,
                headers: tenantHeader
            });
            if (res.data.success) {
                setMsg({ type: "success", text: "Branding updated successfully! The app should reflect changes globally now." });
                await refreshProfile();
            }
        } catch (err) {
            setMsg({ type: "error", text: err.response?.data?.message || "Failed to update branding" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f5f5f5" }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center" }}>
                    <Typography variant="h4" sx={{ fontWeight: "bold", color: "#333", display: "flex", alignItems: "center", gap: 2 }}>
                        <PaletteIcon fontSize="large" sx={{ color: form.primary_color }} /> Branding Setup
                    </Typography>
                </Box>

                {msg.text && <Alert severity={msg.type} sx={{ mb: 3 }}>{msg.text}</Alert>}

                <Paper sx={{ p: 4, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>General Information</Typography>
                            <TextField
                                fullWidth
                                label="Tenant / Company Name"
                                variant="outlined"
                                value={form.tenant_name}
                                onChange={(e) => setForm({ ...form, tenant_name: e.target.value })}
                                sx={{ mb: 4 }}
                            />

                            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>Primary Color</Typography>
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                                {PRESET_COLORS.map(color => (
                                    <Box
                                        key={color}
                                        onClick={() => setForm({ ...form, primary_color: color })}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            bgcolor: color,
                                            cursor: "pointer",
                                            border: form.primary_color === color ? "3px solid #000" : "1px solid #ccc",
                                            boxShadow: form.primary_color === color ? "0 0 0 2px #fff inset" : "none"
                                        }}
                                    />
                                ))}
                            </Box>
                            
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Typography>Custom Hex:</Typography>
                                <input 
                                    type="color" 
                                    value={form.primary_color} 
                                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                                    style={{ width: '60px', height: '40px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                                />
                                <TextField
                                    size="small"
                                    value={form.primary_color}
                                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                                    sx={{ width: 120 }}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>Tenant Logo</Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                <Avatar 
                                    src={form.logo_url} 
                                    alt="Tenant Logo" 
                                    variant="rounded"
                                    sx={{ width: 150, height: 150, border: "2px dashed #ccc", bgcolor: "transparent" }} 
                                >
                                    No Logo
                                </Avatar>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<UploadIcon />}
                                    sx={{ color: form.primary_color, borderColor: form.primary_color }}
                                >
                                    Upload Logo
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleFileChange}
                                    />
                                </Button>
                                <Typography variant="caption" color="textSecondary" align="center">
                                    Recommended size: 200x200px (Max 2MB).
                                    Square or landscape format.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end", gap: 2, borderTop: "1px solid #eee", pt: 3 }}>
                        <Button 
                            variant="contained" 
                            size="large"
                            startIcon={<SaveIcon />}
                            onClick={handleSave} 
                            disabled={loading} 
                            sx={{ bgcolor: form.primary_color, "&:hover": { opacity: 0.9, bgcolor: form.primary_color } }}
                        >
                            Save Branding Settings
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default BrandingSetupPage;
